/** @format */

import React from "react";
import { motion } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
// import { useMediaQuery } from "react-responsive"; // Can often handle via Tailwind classes

// Icons (ensure paths are correct)
import MailChimpSignup from "./MailChimpSignup"; // Assuming path
import {
	ArrowUp,
	Phone,
	Mail,
	MapPin,
	Facebook,
	Instagram,
	Youtube,
	Linkedin,
	ShieldCheck,
	Star,
} from "lucide-react"; // Added more specific icons

// Links data (ensure path is correct)
import {
	helpLinks,
	aboutLinks,
	contactLinks,
	socialLinks,
	shadeLinks,
} from "./links";

// Note: Removed Toast import/state - Newsletter success should ideally be handled within MailChimpSignup component

const Footer = () => {
	// const isMobile = useMediaQuery({ query: '(max-width: 767px)' }); // Use Tailwind's md: prefix instead
	const country = useSelector((state) => state.country); // Ensure correct path
	const location = useLocation();

	// --- Process Links (Consider memoizing if links data is complex or fetched) ---
	const footerSections = [
		{
			title: "Shop Shades", // More action-oriented
			links: shadeLinks.sort((a, b) => a.text.localeCompare(b.text)), // Assuming shadeLinks has { text, to } structure
		},
		{
			title: "Customer Support", // Clearer title
			links: helpLinks.sort((a, b) => a.text.localeCompare(b.text)), // Assuming helpLinks has { text, to } structure
		},
		{
			title: "Our Company", // Clearer title
			links: aboutLinks.sort((a, b) => a.text.localeCompare(b.text)), // Assuming aboutLinks has { text, to } structure
		},
		// Removed separate Contact column, integrate info directly
	];

	// Map social links data to include the component if needed
	const socialMediaLinks = socialLinks.map((link) => ({
		...link,
		IconComponent: link.Icon, // Assuming link.Icon holds the component like Facebook, Instagram etc.
	}));

	const scrollToTop = () => {
		window.scrollTo({ top: 0, behavior: "smooth" });
	};

	// Don't render Footer on specific pages if needed (e.g., checkout steps?)
	// if (location.pathname.startsWith("/checkout")) return null;

	return (
		<>
			{/* --- Section 1: Newsletter Signup --- */}
			<section className="bg-black py-12 md:py-16">
				<div className="container mx-auto px-4">
					<motion.div
						className="max-w-2xl mx-auto text-center"
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true, amount: 0.3 }} // Trigger animation when 30% visible
						transition={{ duration: 0.5 }}
					>
						{/* Provide context for the signup */}
						<h2 className="text-2xl md:text-3xl font-semibold text-white mb-3">
							Stay In The Know
						</h2>
						<p className="text-gray-300 mb-6 text-sm md:text-base">
							Sign up for exclusive offers, new product alerts, design tips, and
							more.
						</p>
						<MailChimpSignup />{" "}
						{/* Component handles its own form logic and feedback */}
					</motion.div>
				</div>
			</section>

			{/* --- Section 2: Main Footer --- */}
			<footer className="bg-secondary text-white pt-12 md:pt-16 pb-8">
				<div className="container mx-auto px-4">
					{/* Grid Layout for Links & Contact Info */}
					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12 mb-10 md:mb-12">
						{/* Link Sections (Generated) */}
						{footerSections.map((section, index) => (
							<motion.div
								key={section.title}
								initial={{ opacity: 0, y: 15 }}
								whileInView={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.4, delay: index * 0.05 }}
								viewport={{ once: true, amount: 0.2 }}
							>
								{/* Use uppercase for visual hierarchy */}
								<h3 className="text-sm font-semibold uppercase tracking-wider text-white/70 mb-4">
									{section.title}
								</h3>
								<ul className="space-y-2">
									{" "}
									{/* Use list for semantics */}
									{section.links.map((link) => (
										<li key={link.text}>
											<Link
												to={link.to}
												className="text-white/80 hover:text-white hover:underline text-sm transition-colors duration-150 focus:outline-none focus:text-white focus:ring-1 focus:ring-primary rounded"
											>
												{link.text}
											</Link>
										</li>
									))}
								</ul>
							</motion.div>
						))}

						{/* Contact Info & Social Links Section */}
						<motion.div
							initial={{ opacity: 0, y: 15 }}
							whileInView={{ opacity: 1, y: 0 }}
							transition={{
								duration: 0.4,
								delay: footerSections.length * 0.05,
							}}
							viewport={{ once: true, amount: 0.2 }}
							className="md:col-span-2 lg:col-span-1" // Span 2 on medium, 1 on large
						>
							<h3 className="text-sm font-semibold uppercase tracking-wider text-white/70 mb-4">
								Get In Touch
							</h3>
							{/* Contact Details List */}
							<ul className="space-y-3 text-sm mb-6">
								{/* Find contact info from your links data or add directly */}
								<li className="flex items-start gap-2 text-white/80">
									<MapPin
										size={16}
										className="mt-0.5 flex-shrink-0 text-primary"
									/>
									<span>50 Hollinger Rd, Toronto, ON M4B 3G4, Canada</span>{" "}
									{/* Replace with actual address */}
								</li>
								<li className="flex items-center gap-2 text-white/80">
									<Phone size={16} className="text-primary" />
									<a
										href="tel:+1416-285-6001"
										className="hover:text-white hover:underline"
									>
										(416) 285-6001
									</a>{" "}
									{/* Replace with actual phone */}
								</li>
								<li className="flex items-center gap-2 text-white/80">
									<Mail size={16} className="text-primary" />
									<a
										href="mailto:info@offcutshades.com"
										className="hover:text-white hover:underline"
									>
										info@offcutshades.com
									</a>{" "}
									{/* Replace with actual email */}
								</li>
							</ul>
							{/* Social Links */}
							<h4 className="text-xs font-semibold uppercase tracking-wider text-white/70 mb-3">
								Follow Us
							</h4>
							<div className="flex items-center gap-4">
								{socialMediaLinks.map((link) => (
									<a
										key={link.text}
										href={link.to}
										target="_blank"
										rel="noopener noreferrer"
										className="text-white/70 hover:text-white hover:scale-110 transition-all duration-150"
										aria-label={link.text} // Accessibility label
										title={link.text} // Tooltip
									>
										{link.IconComponent ? (
											<link.IconComponent size={20} />
										) : (
											<span>{link.text}</span>
										)}
									</a>
								))}
							</div>
						</motion.div>
					</div>

					{/* --- Section 3: Bottom Bar --- */}
					<motion.div
						className="mt-10 pt-6 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4"
						initial={{ opacity: 0 }}
						whileInView={{ opacity: 1 }}
						viewport={{ once: true }}
						transition={{ duration: 0.5, delay: 0.2 }}
					>
						{/* Copyright & Currency */}
						<div className="flex items-center gap-2 text-center md:text-left">
							<img
								src="/images/logoDark-1.png"
								alt=""
								className="h-6 hidden sm:inline-block"
							/>{" "}
							{/* Hide logo on very small screens */}
							<p className="text-xs text-white/60">
								© {new Date().getFullYear()} Off Cut Shades. All Rights
								Reserved. <br className="sm:hidden" />
								Prices in{" "}
								<span className="font-medium">
									{country === "Canada" ? "CAD" : "USD"}
								</span>
								.
							</p>
						</div>

						{/* Back to Top */}
						<motion.button
							onClick={scrollToTop}
							className="flex items-center gap-1.5 text-xs text-white/60 hover:text-white transition-colors duration-150"
							aria-label="Scroll back to top"
							whileHover={{ y: -2 }}
							whileTap={{ scale: 0.95 }}
						>
							<span>Back to top</span>
							<ArrowUp size={14} />
						</motion.button>
					</motion.div>
				</div>
			</footer>

			{/* Toast should be rendered at the App level or within MailChimpSignup */}
			{/* Removed Toast from Footer */}
		</>
	);
};

export default Footer;
